import React, { useState, useEffect, useMemo } from 'react';
import { getFileUrl } from '../../utils/fileUtil';

// Monday.com-style table view: every board column is a colored, collapsible
// group; every card is an item row; a row expands to reveal its sub-items
// (child cards linked via `parent`) and its checklist (`subtasks`).

const GROUP_PALETTE = [
  '#579bfc', // blue
  '#00c875', // green
  '#fdab3d', // orange
  '#e2445c', // red
  '#a25ddc', // purple
  '#0086c0', // dark blue
  '#ff642e', // dark orange
  '#037f4c', // dark green
  '#784bd1', // violet
  '#ffcb00', // yellow
  '#66ccff', // sky
  '#bb3354'  // berry
];

const KNOWN_GROUP_COLORS = {
  'Backlog': '#c4c4c4',
  'Todo': '#579bfc',
  'To Do': '#579bfc',
  'In Progress': '#fdab3d',
  'Waiting For': '#a25ddc',
  'Done': '#00c875',
  'Cancelled': '#e2445c'
};

const groupColor = (column, index) => KNOWN_GROUP_COLORS[column] || GROUP_PALETTE[index % GROUP_PALETTE.length];

const PRIORITY_COLORS = {
  'Low': '#00c875',
  'Medium': '#579bfc',
  'High': '#fdab3d',
  'Urgent': '#e2445c'
};

const parseDueDate = (dueDate) => {
  if (!dueDate) return null;
  const datePart = typeof dueDate === 'string' ? dueDate.split('T')[0] : null;
  if (!datePart) return new Date(dueDate);
  const [y, m, d] = datePart.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const formatDue = (dueDate) => {
  const d = parseDueDate(dueDate);
  if (!d) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const isOverdue = (dueDate, completedAt) => {
  if (completedAt) return false;
  const d = parseDueDate(dueDate);
  if (!d) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d < today;
};

const toDateInputValue = (dueDate) => {
  if (!dueDate) return '';
  return typeof dueDate === 'string' ? dueDate.split('T')[0] : new Date(dueDate).toISOString().split('T')[0];
};

const Avatar = ({ user }) => (
  <div
    className="w-6 h-6 rounded-full bg-primary-blue text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0 overflow-hidden ring-2 ring-white dark:ring-gray-900"
    title={user.name}
  >
    {user.avatar ? (
      <img src={getFileUrl(user.avatar)} alt="" className="h-full w-full object-cover" />
    ) : (
      (user.name || '?').charAt(0).toUpperCase()
    )}
  </div>
);

const OwnerCell = ({ users }) => {
  if (!users || users.length === 0) {
    return (
      <div className="w-6 h-6 rounded-full border border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-300 dark:text-gray-600" title="Unassigned">
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
      </div>
    );
  }
  return (
    <div className="flex items-center -space-x-1.5">
      {users.slice(0, 3).map(u => <Avatar key={u._id} user={u} />)}
      {users.length > 3 && (
        <span className="ml-2 text-[10px] font-semibold text-gray-500 dark:text-gray-400 pl-1">+{users.length - 3}</span>
      )}
    </div>
  );
};

// Monday-style solid status pill. It is a <select> so status changes stay one click.
const StatusCell = ({ value, columns, colorFor, onChange, disabled }) => (
  <div className="relative w-full">
    <select
      value={value}
      disabled={disabled}
      onChange={(e) => { e.stopPropagation(); onChange(e.target.value); }}
      onClick={(e) => e.stopPropagation()}
      className="w-full appearance-none text-center text-[11px] font-semibold text-white px-2 py-1.5 rounded-sm border-0 outline-none cursor-pointer disabled:cursor-default truncate"
      style={{ backgroundColor: colorFor(value) }}
      title={value}
    >
      {columns.map(c => <option key={c} value={c}>{c}</option>)}
    </select>
  </div>
);

const PriorityCell = ({ value, onChange }) => (
  <select
    value={value || 'Medium'}
    onChange={(e) => { e.stopPropagation(); onChange(e.target.value); }}
    onClick={(e) => e.stopPropagation()}
    className="w-full appearance-none text-center text-[11px] font-semibold text-white px-2 py-1.5 rounded-sm border-0 outline-none cursor-pointer"
    style={{ backgroundColor: PRIORITY_COLORS[value] || PRIORITY_COLORS.Medium }}
  >
    {Object.keys(PRIORITY_COLORS).map(p => <option key={p} value={p}>{p}</option>)}
  </select>
);

const DueCell = ({ dueDate, completedAt, onChange }) => {
  const overdue = isOverdue(dueDate, completedAt);
  const label = formatDue(dueDate);
  return (
    <label
      onClick={(e) => e.stopPropagation()}
      className={`relative flex items-center justify-center gap-1 h-7 rounded-sm text-[11px] font-semibold cursor-pointer whitespace-nowrap px-2 ${
        !label
          ? 'text-gray-300 dark:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800'
          : overdue
            ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
            : completedAt
              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
              : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
      }`}
      title={dueDate ? `Due ${new Date(dueDate).toLocaleDateString()}` : 'Set due date'}
    >
      {label || (
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
      )}
      <input
        type="date"
        value={toDateInputValue(dueDate)}
        onChange={(e) => onChange(e.target.value || null)}
        className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
      />
    </label>
  );
};

const ProgressCell = ({ done, total }) => {
  if (!total) return <span className="text-[11px] text-gray-300 dark:text-gray-600">&ndash;</span>;
  const pct = Math.round((done / total) * 100);
  return (
    <div className="flex items-center gap-2 w-full" title={`${done}/${total} done`}>
      <div className="flex-1 h-3 rounded-sm bg-gray-200 dark:bg-gray-700 overflow-hidden">
        <div className="h-full bg-[#00c875] transition-all" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 w-8 text-right">{pct}%</span>
    </div>
  );
};

const LabelsCell = ({ labels }) => {
  const named = (labels || []).filter(l => l.text && l.text.trim());
  if (named.length === 0) return <span className="text-[11px] text-gray-300 dark:text-gray-600">&ndash;</span>;
  return (
    <div className="flex items-center gap-1 overflow-hidden">
      {named.slice(0, 2).map(l => (
        <span
          key={l._id || l.text}
          className="text-[10px] font-bold px-1.5 py-0.5 rounded-full border leading-none truncate max-w-[90px]"
          style={{ backgroundColor: `${l.color}15`, color: l.color, borderColor: `${l.color}30` }}
        >
          {l.text}
        </span>
      ))}
      {named.length > 2 && <span className="text-[10px] font-semibold text-gray-400">+{named.length - 2}</span>}
    </div>
  );
};

const CompleteToggle = ({ completedAt, onToggle, small }) => (
  <button
    onClick={(e) => { e.stopPropagation(); onToggle(); }}
    className={`flex-shrink-0 ${small ? 'w-3.5 h-3.5' : 'w-4 h-4'} rounded-full border-2 flex items-center justify-center transition-colors ${
      completedAt ? 'bg-[#00c875] border-[#00c875] text-white' : 'border-gray-300 dark:border-gray-500 hover:border-[#00c875]'
    }`}
    title={completedAt ? 'Mark incomplete' : 'Mark complete'}
  >
    {completedAt && (
      <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
    )}
  </button>
);

// Shared grid template so header, item rows, and sub-item rows line up.
const GRID = 'minmax(280px, 1fr) 110px 150px 110px 110px 150px 130px';

const ColumnHeaders = ({ color, bulkMode, allSelected, onSelectAll }) => (
  <div
    className="grid items-center border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-[11px] font-semibold text-gray-500 dark:text-gray-400 select-none"
    style={{ gridTemplateColumns: GRID }}
  >
    <div className="flex items-center h-9">
      <div className="w-1.5 self-stretch rounded-l-sm" style={{ backgroundColor: color }} />
      <div className="w-9 flex items-center justify-center">
        {bulkMode && (
          <input
            type="checkbox"
            checked={allSelected}
            onChange={onSelectAll}
            className="w-3.5 h-3.5 accent-blue-600 cursor-pointer"
          />
        )}
      </div>
      <span className="pl-1">Item</span>
    </div>
    <div className="text-center border-l border-gray-100 dark:border-gray-800 h-9 flex items-center justify-center">Owner</div>
    <div className="text-center border-l border-gray-100 dark:border-gray-800 h-9 flex items-center justify-center">Status</div>
    <div className="text-center border-l border-gray-100 dark:border-gray-800 h-9 flex items-center justify-center">Priority</div>
    <div className="text-center border-l border-gray-100 dark:border-gray-800 h-9 flex items-center justify-center">Due</div>
    <div className="text-center border-l border-gray-100 dark:border-gray-800 h-9 flex items-center justify-center">Labels</div>
    <div className="text-center border-l border-gray-100 dark:border-gray-800 h-9 flex items-center justify-center">Progress</div>
  </div>
);

const SubItemRow = ({ card, columns, colorFor, onCardClick, onMoveCard, onUpdateCard, onToggleComplete }) => (
  <div
    onClick={() => onCardClick(card._id)}
    className="grid items-center bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 hover:bg-blue-50/40 dark:hover:bg-gray-800/40 cursor-pointer group"
    style={{ gridTemplateColumns: GRID }}
  >
    <div className="flex items-center h-9 min-w-0">
      <div className="w-1.5 self-stretch" style={{ backgroundColor: `${colorFor(card.column)}66` }} />
      <div className="w-9" />
      <div className="w-6 flex justify-center text-gray-300 dark:text-gray-600">
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5v10a2 2 0 002 2h8m0 0l-3-3m3 3l-3 3" /></svg>
      </div>
      <CompleteToggle small completedAt={card.completedAt} onToggle={() => onToggleComplete(card._id, card.completedAt)} />
      <span className={`ml-2 text-xs truncate group-hover:text-primary-blue ${card.completedAt ? 'line-through text-gray-400 dark:text-gray-500' : 'text-gray-800 dark:text-gray-200'}`}>
        {card.title}
      </span>
    </div>
    <div className="flex justify-center border-l border-gray-100 dark:border-gray-800 h-9 items-center"><OwnerCell users={card.assignedTo} /></div>
    <div className="border-l border-gray-100 dark:border-gray-800 h-9 flex items-center px-1"><StatusCell value={card.column} columns={columns} colorFor={colorFor} onChange={(col) => onMoveCard(card._id, col)} /></div>
    <div className="border-l border-gray-100 dark:border-gray-800 h-9 flex items-center px-1"><PriorityCell value={card.priority} onChange={(p) => onUpdateCard(card._id, { priority: p })} /></div>
    <div className="border-l border-gray-100 dark:border-gray-800 h-9 flex items-center px-1"><DueCell dueDate={card.dueDate} completedAt={card.completedAt} onChange={(d) => onUpdateCard(card._id, { dueDate: d })} /></div>
    <div className="border-l border-gray-100 dark:border-gray-800 h-9 flex items-center px-2"><LabelsCell labels={card.labels} /></div>
    <div className="border-l border-gray-100 dark:border-gray-800 h-9 flex items-center px-2">
      <ProgressCell done={(card.subtasks || []).filter(s => s.completed).length} total={(card.subtasks || []).length} />
    </div>
  </div>
);

const ChecklistRow = ({ cardId, subtask, onToggleSubtask }) => (
  <div
    onClick={(e) => { e.stopPropagation(); onToggleSubtask(cardId, subtask._id); }}
    className="flex items-center h-8 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/40 cursor-pointer"
  >
    <div className="w-1.5 self-stretch bg-gray-200 dark:bg-gray-700" />
    <div className="w-9" />
    <div className="w-6" />
    <input
      type="checkbox"
      readOnly
      checked={!!subtask.completed}
      className="w-3.5 h-3.5 accent-[#00c875] cursor-pointer"
    />
    <span className={`ml-2 text-xs truncate ${subtask.completed ? 'line-through text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'}`}>
      {subtask.content}
    </span>
  </div>
);

const InlineAdd = ({ placeholder, onSubmit, indent = 0, color }) => {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const v = value.trim();
    if (!v || busy) return;
    setBusy(true);
    try {
      await onSubmit(v);
      setValue('');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center h-9 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800" onClick={(e) => e.stopPropagation()}>
      <div className="w-1.5 self-stretch" style={{ backgroundColor: color ? `${color}33` : undefined }} />
      <div className="w-9" />
      {Array.from({ length: indent }).map((_, i) => <div key={i} className="w-6" />)}
      <span className="text-gray-300 dark:text-gray-600 mr-2 text-sm leading-none">+</span>
      <input
        value={value}
        disabled={busy}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') setValue(''); }}
        onBlur={() => { if (value.trim()) submit(); }}
        placeholder={placeholder}
        className="flex-1 bg-transparent text-xs text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 outline-none py-1 pr-3"
      />
    </div>
  );
};

const ItemRow = ({
  card,
  childCards,
  columns,
  colorFor,
  expanded,
  onToggleExpand,
  bulkMode,
  selected,
  onToggleSelect,
  onCardClick,
  onMoveCard,
  onUpdateCard,
  onToggleComplete,
  onToggleSubtask,
  onAddSubtask,
  onAddChildCard
}) => {
  const subtasks = card.subtasks || [];
  const childDone = childCards.filter(c => c.completedAt).length;
  const subDone = subtasks.filter(s => s.completed).length;
  const progressTotal = childCards.length + subtasks.length;
  const progressDone = childDone + subDone;
  const hasSubItems = progressTotal > 0;

  return (
    <>
      <div
        onClick={() => bulkMode ? onToggleSelect(card._id) : onCardClick(card._id)}
        className={`grid items-center border-b border-gray-100 dark:border-gray-800 cursor-pointer group transition-colors ${
          selected && bulkMode ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-white dark:bg-gray-900 hover:bg-blue-50/40 dark:hover:bg-gray-800/40'
        }`}
        style={{ gridTemplateColumns: GRID }}
      >
        <div className="flex items-center h-10 min-w-0">
          <div className="w-1.5 self-stretch" style={{ backgroundColor: colorFor(card.column) }} />
          <div className="w-9 flex items-center justify-center">
            {bulkMode ? (
              <input
                type="checkbox"
                checked={selected}
                onChange={() => onToggleSelect(card._id)}
                onClick={(e) => e.stopPropagation()}
                className="w-3.5 h-3.5 accent-blue-600 cursor-pointer"
              />
            ) : (
              <button
                onClick={(e) => { e.stopPropagation(); onToggleExpand(card._id); }}
                className={`w-5 h-5 rounded flex items-center justify-center transition-colors ${
                  hasSubItems ? 'text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700' : 'text-gray-300 dark:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
                title={expanded ? 'Collapse sub-items' : 'Show sub-items'}
              >
                <svg className={`w-3.5 h-3.5 transition-transform ${expanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" /></svg>
              </button>
            )}
          </div>
          <CompleteToggle completedAt={card.completedAt} onToggle={() => onToggleComplete(card._id, card.completedAt)} />
          {card.coverPhoto && (
            <div className="h-6 w-6 rounded overflow-hidden flex-shrink-0 ml-2">
              <img src={getFileUrl(card.coverPhoto)} alt="" className="h-full w-full object-cover" />
            </div>
          )}
          <span className={`ml-2 text-sm truncate group-hover:text-primary-blue transition-colors ${card.completedAt ? 'line-through text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-gray-100'}`}>
            {card.title}
          </span>
          {hasSubItems && (
            <span
              className={`ml-2 flex-shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                progressDone === progressTotal ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
              }`}
              title={`${childCards.length} sub-item${childCards.length === 1 ? '' : 's'}, ${subtasks.length} checklist item${subtasks.length === 1 ? '' : 's'}`}
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5v10a2 2 0 002 2h8m0 0l-3-3m3 3l-3 3" /></svg>
              {progressDone}/{progressTotal}
            </span>
          )}
        </div>
        <div className="flex justify-center border-l border-gray-100 dark:border-gray-800 h-10 items-center"><OwnerCell users={card.assignedTo} /></div>
        <div className="border-l border-gray-100 dark:border-gray-800 h-10 flex items-center px-1"><StatusCell value={card.column} columns={columns} colorFor={colorFor} onChange={(col) => onMoveCard(card._id, col)} /></div>
        <div className="border-l border-gray-100 dark:border-gray-800 h-10 flex items-center px-1"><PriorityCell value={card.priority} onChange={(p) => onUpdateCard(card._id, { priority: p })} /></div>
        <div className="border-l border-gray-100 dark:border-gray-800 h-10 flex items-center px-1"><DueCell dueDate={card.dueDate} completedAt={card.completedAt} onChange={(d) => onUpdateCard(card._id, { dueDate: d })} /></div>
        <div className="border-l border-gray-100 dark:border-gray-800 h-10 flex items-center px-2"><LabelsCell labels={card.labels} /></div>
        <div className="border-l border-gray-100 dark:border-gray-800 h-10 flex items-center px-2"><ProgressCell done={progressDone} total={progressTotal} /></div>
      </div>

      {expanded && !bulkMode && (
        <div className="bg-gray-50/70 dark:bg-gray-800/30 border-b border-gray-200 dark:border-gray-700">
          {childCards.length > 0 && (
            <div className="flex items-center h-7 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
              <div className="w-1.5 self-stretch" />
              <div className="w-9" />
              <div className="w-6" />
              Sub-items
            </div>
          )}
          {childCards.map(child => (
            <SubItemRow
              key={child._id}
              card={child}
              columns={columns}
              colorFor={colorFor}
              onCardClick={onCardClick}
              onMoveCard={onMoveCard}
              onUpdateCard={onUpdateCard}
              onToggleComplete={onToggleComplete}
            />
          ))}
          <InlineAdd
            indent={1}
            color={colorFor(card.column)}
            placeholder="Add sub-item (creates a linked card)"
            onSubmit={(title) => onAddChildCard(card, title)}
          />

          {subtasks.length > 0 && (
            <div className="flex items-center h-7 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
              <div className="w-1.5 self-stretch" />
              <div className="w-9" />
              <div className="w-6" />
              Checklist
            </div>
          )}
          {subtasks.map(st => (
            <ChecklistRow key={st._id} cardId={card._id} subtask={st} onToggleSubtask={onToggleSubtask} />
          ))}
          <InlineAdd
            indent={1}
            placeholder="Add checklist item"
            onSubmit={(content) => onAddSubtask(card._id, content)}
          />
        </div>
      )}
    </>
  );
};

const TableView = ({
  board,
  cardsByColumn,
  childrenByParent = {},
  onCardClick,
  openAddModal,
  onQuickAddCard,
  bulkMode = false,
  selectedCards = new Set(),
  onToggleCard = () => {},
  onSelectAll = () => {},
  onMoveCard = () => {},
  onUpdateCard = () => {},
  onToggleComplete = () => {},
  onToggleSubtask = () => {},
  onAddSubtask = () => {},
  onAddChildCard = () => {}
}) => {
  const storageKey = `tableView:${board._id}`;
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(`${storageKey}:collapsed`) || '[]');
      return new Set(saved);
    } catch { return new Set(); }
  });
  const [expandedItems, setExpandedItems] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(`${storageKey}:expanded`) || '[]');
      return new Set(saved);
    } catch { return new Set(); }
  });

  useEffect(() => {
    try { localStorage.setItem(`${storageKey}:collapsed`, JSON.stringify([...collapsedGroups])); } catch {}
  }, [collapsedGroups, storageKey]);
  useEffect(() => {
    try { localStorage.setItem(`${storageKey}:expanded`, JSON.stringify([...expandedItems])); } catch {}
  }, [expandedItems, storageKey]);

  const colorFor = useMemo(() => {
    const map = {};
    board.columns.forEach((c, i) => { map[c] = groupColor(c, i); });
    return (col) => map[col] || '#c4c4c4';
  }, [board.columns]);

  // Ids of every card that lives on the board, so a child whose parent is present
  // nests under it rather than showing twice.
  const cardIds = useMemo(() => new Set(board.cards.map(c => c._id)), [board.cards]);
  const parentIdOf = (c) => c.parent && (typeof c.parent === 'object' ? c.parent._id : c.parent);

  const toggleGroup = (column) => setCollapsedGroups(prev => {
    const next = new Set(prev);
    next.has(column) ? next.delete(column) : next.add(column);
    return next;
  });
  const toggleItem = (id) => setExpandedItems(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const allExpanded = board.columns.every(c => !collapsedGroups.has(c));

  return (
    <div className="flex-1 flex flex-col min-h-0 rounded-lg overflow-hidden bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm shadow-sm border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between px-3 h-9 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 flex-shrink-0">
        <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400">
          {board.columns.length} group{board.columns.length === 1 ? '' : 's'}
        </span>
        <button
          onClick={() => setCollapsedGroups(allExpanded ? new Set(board.columns) : new Set())}
          className="text-[11px] font-semibold text-primary-blue hover:underline"
        >
          {allExpanded ? 'Collapse all' : 'Expand all'}
        </button>
      </div>

      <div className="flex-1 overflow-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent">
        <div className="min-w-[1050px] p-3 space-y-5">
          {board.columns.map((column, idx) => {
            const color = colorFor(column);
            const allInColumn = cardsByColumn[column] || [];
            // Top-level rows: cards whose parent is absent from the board. Children render
            // nested under their parent even when the parent sits in another group.
            const rows = allInColumn.filter(c => !cardIds.has(parentIdOf(c)));
            const collapsed = collapsedGroups.has(column);
            const rowIds = rows.map(c => c._id);
            const allSelected = rowIds.length > 0 && rowIds.every(id => selectedCards.has(id));
            const doneCount = allInColumn.filter(c => c.completedAt).length;

            return (
              <section key={column}>
                <header className="flex items-center gap-2 mb-1.5 select-none">
                  <button
                    onClick={() => toggleGroup(column)}
                    className="w-6 h-6 rounded flex items-center justify-center hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
                    style={{ color }}
                    title={collapsed ? 'Expand group' : 'Collapse group'}
                  >
                    <svg className={`w-4 h-4 transition-transform ${collapsed ? '' : 'rotate-90'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" /></svg>
                  </button>
                  <h3 className="text-base font-semibold" style={{ color }}>{column}</h3>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {allInColumn.length} item{allInColumn.length === 1 ? '' : 's'}
                    {doneCount > 0 && ` · ${doneCount} done`}
                  </span>
                  <button
                    onClick={() => openAddModal(column)}
                    className="ml-auto text-[11px] font-semibold text-gray-500 dark:text-gray-400 hover:text-primary-blue px-2 py-0.5 rounded hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
                    title="Add a card to this group with the full form"
                  >
                    + New item
                  </button>
                </header>

                {collapsed ? (
                  <div
                    onClick={() => toggleGroup(column)}
                    className="flex items-center h-10 rounded-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/60"
                  >
                    <div className="w-1.5 self-stretch rounded-l-sm" style={{ backgroundColor: color }} />
                    <span className="px-3 text-xs text-gray-500 dark:text-gray-400">
                      {allInColumn.length} item{allInColumn.length === 1 ? '' : 's'} hidden
                    </span>
                  </div>
                ) : (
                  <div className="rounded-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <ColumnHeaders
                      color={color}
                      bulkMode={bulkMode}
                      allSelected={allSelected}
                      onSelectAll={() => onSelectAll(rowIds)}
                    />
                    {rows.length === 0 && (
                      <div className="flex items-center h-10 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800">
                        <div className="w-1.5 self-stretch" style={{ backgroundColor: `${color}66` }} />
                        <span className="px-9 text-xs text-gray-400 dark:text-gray-500">No items in this group</span>
                      </div>
                    )}
                    {rows.map(card => (
                      <ItemRow
                        key={card._id}
                        card={card}
                        childCards={childrenByParent[card._id] || []}
                        columns={board.columns}
                        colorFor={colorFor}
                        expanded={expandedItems.has(card._id)}
                        onToggleExpand={toggleItem}
                        bulkMode={bulkMode}
                        selected={selectedCards.has(card._id)}
                        onToggleSelect={onToggleCard}
                        onCardClick={onCardClick}
                        onMoveCard={onMoveCard}
                        onUpdateCard={onUpdateCard}
                        onToggleComplete={onToggleComplete}
                        onToggleSubtask={onToggleSubtask}
                        onAddSubtask={onAddSubtask}
                        onAddChildCard={onAddChildCard}
                      />
                    ))}
                    {!bulkMode && onQuickAddCard && (
                      <InlineAdd
                        color={color}
                        placeholder="Add item"
                        onSubmit={(title) => onQuickAddCard(column, title)}
                      />
                    )}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TableView;
